import * as React from "react";
import './musicbumper.css';
import { FaPlay } from "react-icons/fa";
import { FaPause } from "react-icons/fa";
import { FaForward } from "react-icons/fa";
import { FaBackward } from "react-icons/fa";
import { MdReplayCircleFilled } from "react-icons/md";
import songdata from "./musicselection.json"
import { whenVisible } from '../_shared/sketchFrame';
import audioBus from '../_shared/audioBus';

// Stable id this source registers under on the shared mutual-exclusion bus.
const BUS_ID = "bumper";

const AMPLITUDE_MAX = 255;

class Visualizer extends React.Component {
    constructor(props) {
        super(props);

        let songsT = songdata.songs;

        // Shuffle song array
        this.shuffle(songsT);

        const frequencyCount = props.frequencyCount;

        // The bar groups are built ONCE from a fixed BAR count (not from live
        // amplitude data) so React never recreates the ~256 bar nodes. The
        // amplitudes used to live in React state and forced a full re-render on
        // every animation frame; they are now written straight to the DOM via
        // refs inside animate(). Only state that changes on user actions
        // (play/pause, song title) stays in React state.
        this.state = {
            context: null,
            songs: songsT,
            currentSong: songsT[0].source,
            songTitle: songsT[0].title,
            songPos: 0,
            isPlaying: false
        };

        this.pausePlay = this.pausePlay.bind(this);
        this.pauseSelf = this.pauseSelf.bind(this);
        this.getLevel = this.getLevel.bind(this);
        this.replay = this.replay.bind(this);
        this.playNextSong = this.playNextSong.bind(this);
        this.playPrevSong = this.playPrevSong.bind(this);

        // Bind the animation loop ONCE and reuse it every frame (no per-frame
        // allocation), and keep the requestAnimationFrame id so it can be
        // cancelled. _running gates the loop so the visibility observer can
        // stop/restart it without leaking frames.
        this._animate = this.animate.bind(this);
        this._rafId = null;
        this._running = false;
        this._cleanupFrame = null;
        this.visualizerRef = React.createRef();

        // Throttle the analyser->bar update to ~33fps. rAF is left running every
        // display frame (so it stays in sync with the compositor), but the
        // analyser read + the ~256 per-frame DOM writes only happen when at
        // least _frameInterval ms have elapsed. The spectrum does not change
        // fast enough for the difference vs 60fps to be perceptible.
        this._frameInterval = 30; // ms (~33fps)
        this._lastFrameTime = 0;

        // Single reusable buffer for the analyser's frequency data so we don't
        // allocate a new Uint8Array on every animation frame.
        this._frequencyData = new Uint8Array(frequencyCount);

        // Fixed-length zero array so the bars exist immediately on first render.
        // (The old code used Array.from(frequencyCount), which yields an EMPTY
        // array because a number is not iterable, so no bars rendered until the
        // first animation frame.)
        const zeroAmplitudes = new Array(frequencyCount).fill(0);

        // Build the bar ordering ONCE. The original render reversed the
        // amplitude array, then concatenated the forward array, accumulating an
        // animation-delay value as it went (+15 per bar on the first half, then
        // -15 per bar on the second half). We reproduce the exact same per-bar
        // animationDelay values here, keyed by a stable index, so the visuals
        // and stagger are identical -- but built only once.
        const barCount = zeroAmplitudes.length * 2;
        const barDelays = new Array(barCount);
        const aniDur = 8 * 1000;
        let delay = 0;
        let idx = 0;
        for (let i = 0; i < zeroAmplitudes.length; i++) {
            delay += 15;
            barDelays[idx++] = delay;
        }
        for (let i = 0; i < zeroAmplitudes.length; i++) {
            delay -= 15;
            barDelays[idx++] = delay;
        }
        this._barMeta = barDelays.map((d) => ({ aniDelay: d, aniDur: aniDur }));

        // The amplitude index each bar reads from, in render order: the first
        // half is the reversed amplitude array, the second half is the forward
        // amplitude array.
        const n = zeroAmplitudes.length;
        const ampIndex = new Array(barCount);
        for (let i = 0; i < n; i++) ampIndex[i] = n - 1 - i;         // reversed
        for (let i = 0; i < n; i++) ampIndex[n + i] = i;             // forward
        this._ampIndex = ampIndex;
        this._barCount = barCount;

        // Per-group ref arrays. The same bar markup is rendered in FOUR places
        // (top .bawrap bars + inverted, bottom .bawrap.flip-down bars +
        // inverted), so each group needs its own set of DOM-node refs.
        this._barRefs = {
            topBars: new Array(barCount).fill(null),
            topInverted: new Array(barCount).fill(null),
            bottomBars: new Array(barCount).fill(null),
            bottomInverted: new Array(barCount).fill(null)
        };

        this._registerBar = this._registerBar.bind(this);
    }

    // Ref-callback factory: register a bar DOM node by group + index.
    _registerBar(group, index) {
        return (el) => {
            this._barRefs[group][index] = el;
        };
    }

    // Start the rAF loop if it isn't already running.
    startAnimation() {
        if (!this._running) {
            this._running = true;
            this._rafId = requestAnimationFrame(this._animate);
        }
    }

    // Stop the rAF loop and drop any pending frame.
    stopAnimation() {
        this._running = false;
        if (this._rafId != null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
    }

    shuffle(array) {
        let currentIndex = array.length, randomIndex;

        // While there remain elements to shuffle.
        while (currentIndex > 0) {

            // Pick a remaining element.
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;

            // And swap it with the current element.
            [array[currentIndex], array[randomIndex]] = [
                array[randomIndex], array[currentIndex]];
        }

        return array;
    }

    pausePlay() {
        let audioSource = document.getElementById("drbumper");
        if (!audioSource) return;

        const ctx = this.state.context;

        // Decide intent from the <audio> element's ACTUAL paused state rather
        // than AudioContext.state: a freshly-created context can report
        // 'running' while nothing is playing, which used to send the very first
        // "play" click into the pause branch (so it never started).
        if (audioSource.paused) {
            // START — a suspended context routes the media element to silence,
            // so resume it before playing.
            if (ctx && ctx.state === 'suspended') ctx.resume();
            audioSource.play();

            this.setState({
                isPlaying: true
            });

            // We are starting to play — claim the bus so any other source that
            // is currently playing gets paused, and register pauseSelf so we get
            // paused when some OTHER source later claims it. Also expose getLevel
            // so audioBus.getLevel() (the dancers) tracks our spectrum.
            audioBus.claim(BUS_ID, this.pauseSelf, this.getLevel);
        } else {
            // STOP — suspend the context, pause the element, release the bus.
            if (ctx && ctx.state === 'running') ctx.suspend();
            audioSource.pause();

            this.setState({
                isPlaying: false
            });

            // The user is pausing/stopping us — give up ownership of the bus.
            audioBus.release(BUS_ID);
        }
    }

    // Pause THIS source without touching the bus. Invoked by audioBus when a
    // different source claims ownership, so it must NOT call back into the bus
    // (no claim/release) -- that's the bus's job. Mirrors the pause branch of
    // pausePlay: suspend the AudioContext, pause the <audio>, clear isPlaying.
    pauseSelf() {
        const audioSource = document.getElementById("drbumper");

        if (this.state.context && this.state.context.state === 'running') {
            this.state.context.suspend();
        }
        if (audioSource) {
            audioSource.pause();
        }
        if (this.state.isPlaying) {
            this.setState({
                isPlaying: false
            });
        }
    }

    // Current OVERALL loudness as a single 0..1 value, for the shared audioBus
    // level provider (the faceless dancers read it via audioBus.getLevel()).
    // Averages every bin of the SAME analyser that drives the bar visualizer,
    // reusing its frequency buffer (no per-frame allocation). Returns 0 when the
    // analyser isn't wired yet, so the bus can poll unconditionally.
    getLevel() {
        const analyzer = this.analyzer;
        const data = this._frequencyData;
        if (!analyzer || !data) return 0;
        analyzer.getByteFrequencyData(data);
        const n = data.length;
        if (!n) return 0;
        let sum = 0;
        for (let i = 0; i < n; i++) sum += data[i];
        return sum / n / AMPLITUDE_MAX; // mean bin / 255 -> 0..1
    }

    replay() {
        let audioSource = document.getElementById("drbumper");

        if (audioSource) {
            audioSource.currentTime = 0;
        }
    }

    playNextSong() {
        let audioSource = document.getElementById("drbumper");

        // this.state.context.suspend();
        // audioSource.pause();

        if (this.state.isPlaying)
            this.pausePlay();

        if (this.state.songPos + 1 < this.state.songs.length) {
            this.setState({
                currentSong: this.state.songs[this.state.songPos + 1].source,
                songTitle: this.state.songs[this.state.songPos + 1].title,
                songPos: this.state.songPos + 1
            }, () => {
                try {
                    this.pausePlay();
                } catch (e) {
                    console.log(e);
                }
            });
        }
        else {
            this.setState({
                currentSong: this.state.songs[0].source,
                songTitle: this.state.songs[0].title,
                songPos: 0
            }, () => {
                try {
                    this.pausePlay();
                } catch (e) {
                    console.log(e);
                }
            });
        }

        // this.state.context.resume();
        // audioSource.play();
    }

    playPrevSong() {
        let audioSource = document.getElementById("drbumper");

        // this.state.context.suspend();
        // audioSource.pause();
        if (this.state.isPlaying)
            this.pausePlay();

        if (this.state.songPos - 1 >= 0) {
            this.setState({
                currentSong: this.state.songs[this.state.songPos - 1].source,
                songTitle: this.state.songs[this.state.songPos - 1].title,
                songPos: this.state.songPos - 1
            }, () => {
                this.pausePlay();
            });
        }
        else {
            this.setState({
                currentSong: this.state.songs[this.state.songs.length - 1].source,
                songTitle: this.state.songs[this.state.songs.length - 1].title,
                songPos: this.state.songs.length - 1
            }, () => {
                this.pausePlay();
            });
        }

        // this.pausePlay();

        // this.state.context.resume();
        // audioSource.play();
    }

    // Ensure this.audio has been set by ref callback
    componentDidMount() {
        if (this.state.currentSong && typeof window !== 'undefined') {
            // Wire up our audio
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.source = this.audioContext.createMediaElementSource(this.audioRef);
            this.volumeControl = this.audioContext.createGain();
            this.source.connect(this.audioContext.destination);
            this.source.connect(this.volumeControl);

            // Create our analyZer 🇺🇸
            this.analyzer = this.audioContext.createAnalyser();
            this.analyzer.fftSize = this.props.frequencyCount * 2;
            this.volumeControl.connect(this.analyzer);
            this.analyzer.connect(this.audioContext.destination);

            // Analyzer will know user's set volume
            this.volumeControl.gain.value = this.audioRef.volume;

            // Kick off our animation only while the player is on-screen AND the
            // tab is visible. onEnter starts the loop, onLeave stops it.
            this._cleanupFrame = whenVisible(
                this.visualizerRef.current,
                () => this.startAnimation(),
                () => this.stopAnimation()
            );
            console.log(this.audioContext);
            this.setState({
                context: this.audioContext
            });
        }
    }

    componentWillUnmount() {
        // Detach the visibility observer + visibilitychange listener first...
        if (this._cleanupFrame) {
            this._cleanupFrame();
            this._cleanupFrame = null;
        }
        // ...then stop the animation loop / drop any pending frame.
        this.stopAnimation();

        // If we still own the bus, release it so we don't leave a stale
        // pauseSelf callback pointing at this now-unmounted component.
        audioBus.release(BUS_ID);
    }

    // Constantly get frequency data from audio so that amplitude can be updated.
    // Writes bar sizes DIRECTLY to the DOM via refs -- NO setState -- so the
    // hundreds of bar nodes are never reconciled per frame.
    //
    // Sizing is applied as a GPU-composited `transform: scaleY()` (via the --sy
    // custom property the CSS reads) instead of writing `height`. Animating
    // height forced layout + paint on every one of the ~256 bar nodes every
    // frame -- the dominant source of lag, especially in the large full-screen
    // view. scaleY is handled entirely on the compositor: no layout, no paint.
    // The values written are the old percentage heights divided by 100, so the
    // rendered bar geometry is pixel-identical.
    //
    // The loop is throttled to ~33fps: the audio spectrum does not change fast
    // enough for 60fps to be visible, and halving the per-frame DOM writes
    // (256 property sets) roughly halves the visualizer's main-thread cost.
    animate(now) {
        // Bail if we've been gated off (off-screen / hidden tab) or torn down.
        if (!this._running) {
            return;
        }

        // Reuse the one bound reference (no new closure allocated each frame).
        this._rafId = requestAnimationFrame(this._animate);

        // Throttle to ~33fps. rAF still runs every display frame (cheap), but we
        // only sample the analyser + rewrite the bars about every 30ms.
        const t = (typeof now === 'number') ? now : 0;
        if ((t - this._lastFrameTime) < this._frameInterval) {
            return;
        }
        this._lastFrameTime = t;

        // Reuse the single buffer instead of allocating a new Uint8Array per frame.
        this.analyzer.getByteFrequencyData(this._frequencyData);

        const freq = this._frequencyData;
        const ampIndex = this._ampIndex;
        const barCount = this._barCount;
        const refs = this._barRefs;
        const topBars = refs.topBars;
        const topInverted = refs.topInverted;
        const bottomBars = refs.bottomBars;
        const bottomInverted = refs.bottomInverted;
        const INV = 1 / AMPLITUDE_MAX;

        for (let i = 0; i < barCount; i++) {
            // Raw amplitude byte, mapped to the same 0-1 scaleY the old code
            // expressed as a 0-100% height (height% / 100 === scaleY).
            const raw = freq[ampIndex[i]];
            const sy = raw * INV;

            // Matches the old InvertedBar height formula exactly, as a 0-1 scale:
            //   amplitude<=0 -> 100%          -> 1
            //   amplitude>0  -> (100-amp-7)%  -> (93 - amp) / 100
            // where amp = raw * 100 / AMPLITUDE_MAX.
            const invSy = (raw <= 0) ? 1 : (0.93 - raw * INV);

            const tb = topBars[i];
            if (tb) tb.style.setProperty('--sy', sy);
            const bb = bottomBars[i];
            if (bb) bb.style.setProperty('--sy', sy);

            const ti = topInverted[i];
            if (ti) ti.style.setProperty('--sy', invSy);
            const bi = bottomInverted[i];
            if (bi) bi.style.setProperty('--sy', invSy);
        }
    }

    // Render one group of <Bar> nodes (stable keys, registered by ref).
    _renderBars(group) {
        const meta = this._barMeta;
        const register = this._registerBar;
        const out = new Array(this._barCount);
        for (let i = 0; i < this._barCount; i++) {
            out[i] = (
                <Bar
                    key={i}
                    registerRef={register(group, i)}
                    aniDelay={meta[i].aniDelay}
                    aniDur={meta[i].aniDur}
                />
            );
        }
        return out;
    }

    // Render one group of <InvertedBar> nodes (stable keys, registered by ref).
    _renderInvertedBars(group) {
        const register = this._registerBar;
        const out = new Array(this._barCount);
        for (let i = 0; i < this._barCount; i++) {
            out[i] = (
                <InvertedBar
                    key={i}
                    registerRef={register(group, i)}
                />
            );
        }
        return out;
    }

    render() {
        const topBars = this._renderBars('topBars');
        const topInverted = this._renderInvertedBars('topInverted');
        const bottomBars = this._renderBars('bottomBars');
        const bottomInverted = this._renderInvertedBars('bottomInverted');

        return (
            <div id="visualizer" ref={this.visualizerRef} className={this.state.isPlaying ? "is-playing" : ""}>
                <div id="controls">
                    {/* add 'controls' prop with no val to see controls */}

                    {/* The Angelic Bumper owns the room: the instant its audio
                        actually starts, claim the shared bus so EVERY other source
                        (StaticFM, the tape deck) is paused; release it when the
                        bumper stops. Tying this to the element's real play/pause
                        events (not the toggle's AudioContext state) makes the
                        mutual-exclusion bulletproof. */}
                    <audio id='drbumper'
                        onEnded={(e) => { this.playNextSong(); }}
                        onPlay={() => audioBus.claim(BUS_ID, this.pauseSelf, this.getLevel)}
                        onPause={() => audioBus.release(BUS_ID)}
                        loop={false} ref={element => this.audioRef = element} src={this.state.currentSong} autoPlay={false} crossOrigin="anonymous">
                    </audio>


                    {/* <button className="play-button" onClick={this.pausePlay}>
                        ??
        </button> */}
                </div>

                <div className="dancing-shapes" onClick={this.pausePlay}>
                    <div className="box">
                    </div>
                    <div className="box">
                    </div>
                    <div className="box">
                    </div>

                    <div className="box">
                    </div>
                    <div className="box">
                    </div>
                </div>

                <div className="now-playing-wrap">
                    <span className="now-playing-left">{"- NOW PLAYING: " + this.state.songTitle}</span>
                    <span className="now-playing-left">{"- NOW PLAYING: " + this.state.songTitle}</span>
                </div>

                <div className="eyewrap">
                    {
                        !this.state.isPlaying
                            ?
                            <div className="not-playing">
                                <p>
                                    - press play -
                                </p>
                            </div>
                            :
                            null
                    }
                    <div className="bawrap">
                        <div className="bars-wrapper-upside-down-inverted">
                            {/* <div id="inverted-bars" className="flip">
                                {invertedBars}
                            </div> */}

                            <div id="inverted-bars" className="unflip">
                                {topInverted}
                            </div>
                        </div>
                        <div className="bars-wrapper">
                            {/* <div id="bars" className="flip">
                                {bars}
                            </div> */}

                            <div id="bars" className="unflip">
                                {topBars}
                            </div>
                        </div>
                    </div>

                    <div className="bawrap flip-down">
                        <div className="bars-wrapper-upside-down-inverted">
                            {/* <div id="inverted-bars" className="flip">
                                {invertedBars}
                            </div> */}

                            <div id="inverted-bars" className="unflip">
                                {bottomInverted}
                            </div>
                        </div>
                        <div className="bars-wrapper">
                            {/* <div id="bars" className="flip">
                                {bars}
                            </div> */}

                            <div id="bars" className="unflip">
                                {bottomBars}
                            </div>
                        </div>
                    </div>
                </div>


                <div className="now-playing-wrap">
                    <span className="now-playing-right">{"- NOW PLAYING: " + this.state.songTitle}</span>
                    <span className="now-playing-right">{"- NOW PLAYING: " + this.state.songTitle}</span>
                </div>

                <div id="controls">
                    <hr></hr>

                    <button onClick={this.playPrevSong}>
                        <FaBackward />
                    </button>

                    <button onClick={this.pausePlay}>
                        {(
                            this.state.isPlaying ?
                                <FaPause />
                                :
                                <FaPlay />
                        )}
                    </button>

                    <button onClick={this.replay}>
                        <MdReplayCircleFilled />
                    </button>

                    <button onClick={this.playNextSong}>
                        <FaForward />
                    </button>

                    <hr></hr>
                </div>


                <div className="dancing-shapes" onClick={this.pausePlay}>
                    <div className="box">
                    </div>
                    <div className="box">
                    </div>
                    <div className="box">
                    </div>

                    <div className="box">
                    </div>
                    <div className="box">
                    </div>
                </div>

            </div >
        )
    }
}

function Bar({ registerRef, aniDelay, aniDur }) {
    // Size is driven by the --sy custom property (transform: scaleY) written by
    // animate(); it starts at 0 to match the old initial height:'0%'.
    return <div ref={registerRef} className="bar" style={{ "--sy": 0, animationDelay: aniDelay + "ms", animationDuration: aniDur + "ms" }}></div>
}

function InvertedBar({ registerRef }) {
    // Starts at full size (--sy:1) to match the old initial height:'100%'.
    return <div ref={registerRef} className="inverted-bar" style={{ "--sy": 1 }} ></ div >
}

// Forward a ref straight through to the inner Visualizer instance so callers
// (e.g. the Angelic Bumper mini player's own play/pause button) can drive
// playback imperatively via visualizer.pausePlay() when the bumper's built-in
// transport is out of reach.
const MusicBumper = React.forwardRef(function MusicBumper(props, ref) {
    return (
        <div className="bumper-wrap">
            <Visualizer ref={ref} frequencyCount={32} audioSource={null} />
        </div>
    );
});

export default MusicBumper;
